'use client';

import type { ReactNode } from 'react';
import type { CardGridSpec } from '../cards/gridSpecs';
import { ContentRail } from '../ui/ContentRail';

/**
 * Layout shell for a set of course cards, driven by the card variant's
 * {@link CardGridSpec}: a bordered list, a horizontal {@link ContentRail},
 * or a container-query grid.
 *
 * <p>Extracted from {@code CourseRow} so the tabbed variant
 * ({@code CourseRowTabs}) renders its panels through exactly the same
 * layout rules — the two must not drift.
 */
export function CourseCardContainer({
  spec,
  children,
  label,
  scrollMode,
  heading,
  headerAction,
}: {
  spec: CardGridSpec;
  children: ReactNode;
  label: string;
  scrollMode?: 'drag' | 'buttons';
  heading?: ReactNode;
  headerAction?: ReactNode;
}) {
  if (spec.kind === 'list') {
    const arr = Array.isArray(children) ? children : [children];
    return (
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: 'none',
          borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
        }}
      >
        {arr.map((child, i) => (
          <li key={i}>{child}</li>
        ))}
      </ul>
    );
  }

  if (spec.kind === 'rail') {
    return (
      <ContentRail
        ariaLabel={label || 'Courses'}
        itemWidth={spec.cardWidth ?? '320px'}
        gap={spec.gap ?? 'clamp(10px, 1.5vw, 16px)'}
        scrollMode={scrollMode}
        heading={heading}
        headerAction={headerAction}
      >
        {children}
      </ContentRail>
    );
  }

  const gap = spec.gap ?? 'clamp(12px, 2vw, 16px)';
  const minW = spec.minCardWidth;
  const multiRowBreak = minW * 4;

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
.lms-cg-wrap { container-type: inline-size; }
.lms-cg {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(${minW}px, 100%), 1fr));
  grid-template-rows: 1fr;
  grid-auto-rows: 0;
  /* overflow: hidden; */
}
@container (max-width: ${multiRowBreak}px) {
  .lms-cg {
    grid-template-rows: auto;
    grid-auto-rows: auto;
    overflow: visible;
  }
}
      `,
        }}
      />
      <div className="lms-cg-wrap">
        <div className="lms-cg" style={{ gap }}>
          {children}
        </div>
      </div>
    </>
  );
}
